import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { RefreshCw, AlertCircle, Users } from 'lucide-react';
import type { FaceRecognitionOpTypeBreakdown } from '../../api/face-recognition.schemas';

interface OpTypeBreakdownChartProps {
  data?: FaceRecognitionOpTypeBreakdown;
  loading?: boolean;
  error?: Error;
  onRetry?: () => void;
}

/**
 * OpTypeBreakdownChart Component
 * 
 * Displays a chart showing Face Recognition operation type breakdown.
 * Shows Enroll vs Match operation distribution.
 * Only displayed when breakdown data is available from BFF.
 * 
 * Requirements: 2.6, 6.2
 */
export function OpTypeBreakdownChart({ 
  data, 
  loading = false, 
  error,
  onRetry
}: OpTypeBreakdownChartProps) {
  
  // Loading state
  if (loading) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Operation Type</CardTitle>
          </div>
          <CardDescription>Enroll vs Match operations</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 p-6 pt-0 space-y-4">
          <div className="space-y-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1">
                    <Skeleton className="w-3 h-3 rounded-full" />
                    <div className="flex-1">
                      <Skeleton className="h-4 w-12 mb-1" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                  <Skeleton className="h-4 w-20" />
                </div>
                <Skeleton className="w-full h-3 rounded-full" />
              </div>
            ))}
          </div>
          <div className="p-3 bg-muted/50 rounded-lg">
            <Skeleton className="h-4 w-24 mb-2" />
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="text-center space-y-1">
                  <Skeleton className="h-4 w-8 mx-auto" />
                  <Skeleton className="h-3 w-12 mx-auto" />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Operation Type</CardTitle>
          </div>
          <CardDescription>Failed to load operation type data</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="mx-auto w-12 h-12 bg-gradient-to-br from-destructive/10 to-destructive/5 rounded-xl flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive/60" />
            </div>
            <div className="space-y-2">
              <h3 className="font-medium">Unable to display chart</h3>
              <p className="text-sm text-muted-foreground">{error.message}</p>
            </div>
            {onRetry && (
              <Button onClick={onRetry} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // No data state
  if (!data) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Operation Type</CardTitle>
          </div>
          <CardDescription>Enroll vs Match operations</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <Users className="h-6 w-6 mx-auto mb-2" />
            <p>No operation type data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate total and percentages
  const total = data.enroll + data.match;
  const opTypes = [
    { name: 'Enroll', value: data.enroll, color: 'bg-purple-500', textColor: 'text-purple-700', description: 'New user registrations' },
    { name: 'Match', value: data.match, color: 'bg-teal-500', textColor: 'text-teal-700', description: 'User verifications' },
  ].filter(item => item.value > 0); // Only show non-zero values

  if (total === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Operation Type</CardTitle>
          </div>
          <CardDescription>Enroll vs Match operations</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <Users className="h-6 w-6 mx-auto mb-2" />
            <p>No operation type data recorded</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">Operation Type</CardTitle>
        </div>
        <CardDescription>
          {total.toLocaleString()} total operations
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 space-y-4 overflow-y-auto">
          {opTypes.map((item, index) => {
            const percentage = total > 0 ? (item.value / total) * 100 : 0;
            
            return (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${item.color}`} />
                    <div className="min-w-0 flex-1">
                      <span className="font-medium">{item.name}</span>
                      <p className="text-xs text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <span className="font-semibold">{item.value.toLocaleString()}</span>
                    <span className="text-muted-foreground ml-1">({percentage.toFixed(1)}%)</span>
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className={`${item.color} h-3 rounded-full transition-all duration-500 ease-out`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Summary */}
        <div className="mt-4 p-3 bg-muted/50 rounded-lg flex-shrink-0">
          <h4 className="font-medium text-sm mb-2">Operation Summary</h4>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div className="text-center">
              <div className="font-semibold text-purple-600">{data.enroll}</div>
              <div className="text-muted-foreground">Enrollments</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-teal-600">{data.match}</div>
              <div className="text-muted-foreground">Matches</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}